import { DOCUMENT } from '@angular/common';
import {
  AfterContentInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges
} from '@angular/core';

import {
  BehaviorSubject,
  Subject,
  Subscription,
  animationFrameScheduler,
  combineLatest,
  debounceTime,
  delay,
  fromEvent,
  startWith,
  takeUntil,
  throttleTime
} from 'rxjs';

import {
  BreakpointKey,
  BreakpointService,
  PlatformService,
  ResizeObserverWrapperService
} from '@hdms/shared/util-core';

import { FloatingStackingService } from './floating-stacking.service';
import { FloatingMode, FloatingModeKey } from './floating.types';

@Directive({
  selector: '[shrdUicFloating]'
})
export class FloatingDirective implements AfterContentInit, OnChanges, OnDestroy {
  /* Inputs */
  @Input() stacking = false;

  @Input() hasPlaceholder = true;
  @Input() containerThreshold = 0;
  @Input() enabledMinBreakpoint: BreakpointKey;
  @Input() enabledMaxBreakpoint: BreakpointKey;
  @Input() bottomed = false;

  @Input() container: HTMLElement | string;
  @Input() shadowElement: HTMLElement | string;

  /* Outputs */
  @Output() floatingChanged = new EventEmitter<boolean>();

  /* Host Bindings */
  @HostBinding('class.shrd-uic-floating') private readonly elementClass = true;

  public isSmartBar = false;
  public readonly floatingElement: HTMLElement;

  /* Observables */
  private readonly offset$ = new BehaviorSubject<number>(0);
  private readonly mode$ = new BehaviorSubject<FloatingModeKey>(FloatingMode.AUTO);
  private readonly unsubscribe$ = new Subject<void>();
  private scrollSubscription: Subscription;

  /* Other props */
  private readonly placeholderClass = 'shrd-uic-floating--placeholder';
  private readonly shadowClass = 'shrd-uic-floating-shadow';
  private readonly floatingClass = 'is-floating';
  private readonly fixedBottomClass = 'fixed-bottom';
  private containerReference: HTMLElement;
  private scrollContainer: HTMLElement | Window;
  private placeholder?: HTMLElement;
  private calculatedOffset: number;
  private floating = false;

  constructor(
    { nativeElement }: ElementRef,
    private readonly floatingStackingService: FloatingStackingService,
    private readonly renderer: Renderer2,
    private readonly breakpointService: BreakpointService,
    private readonly resizeObserver: ResizeObserverWrapperService,
    private readonly platformService: PlatformService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.floatingElement = nativeElement;
  }

  @Input() set offset(offset: number) {
    this.offset$.next(offset);
  }
  @Input() set mode(mode: FloatingModeKey) {
    this.mode$.next(mode);
  }

  @HostBinding('class.full-page-width')
  private get isBottomedFullWidth(): boolean {
    return this.isSmartBar && this.bottomed;
  }

  private set isFloating(val: boolean) {
    this.floating = val;
    if (val) {
      this.renderer.addClass(this.shadowElement, this.shadowClass);
      this.renderer.addClass(this.floatingElement, this.floatingClass);
    } else {
      this.renderer.removeClass(this.shadowElement, this.shadowClass);
      this.renderer.removeClass(this.floatingElement, this.floatingClass);
    }
  }

  private get isFloating(): boolean {
    return this.floating;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.container) {
      this.setContainer(this.container);
      if (this.bottomed) {
        if (this.platformService.isServer) {
          this.renderer.addClass(this.floatingElement, this.fixedBottomClass);
        } else {
          this.setStyle('position', 'relative', this.containerReference);
          this.renderer.removeClass(this.floatingElement, this.fixedBottomClass);
        }
      }
    }
  }

  ngAfterContentInit(): void {
    if (this.platformService.isServer) {
      return;
    }
    this.scrollContainer = this.floatingStackingService.getScrollcontainer();

    if (this.hasPlaceholder) {
      this.createPlaceholder();
    }

    /* Use document.body if no container was provided */
    if (!this.containerReference) {
      this.containerReference = this.document.body;
    }

    /* Check if there is a dedicated shadow element defined */
    if (typeof this.shadowElement === 'string') {
      this.shadowElement = this.floatingElement.querySelector<HTMLElement>(this.shadowElement);
    }
    if (!this.shadowElement) {
      this.shadowElement = this.floatingElement;
    }

    this.addChangeSubscriptions();
  }

  ngOnDestroy(): void {
    this.floatingStackingService.deregisterElement(this.floatingElement);
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.resizeObserver.unobserve(this.containerReference);
    this.placeholder && this.renderer.removeChild(this.renderer.parentNode(this.placeholder), this.placeholder);
    this.removeStyle('position', this.containerReference);
  }

  public update(): void {
    if (this.platformService.isServer) {
      return;
    }
    const mode = this.mode$.getValue();
    /* Check if floating is enabled for breakpoint */
    const withinEnabledRange = this.breakpointService.isWithinBreakpoints(
      this.enabledMinBreakpoint,
      this.enabledMaxBreakpoint
    );

    if (mode === FloatingMode.AUTO && withinEnabledRange) {
      !this.scrollSubscription && this.createScrollSubscription();
      this.checkFloating(true);
    } else {
      this.scrollSubscription && this.destroyScrollSubscription();
      if (mode === FloatingMode.ON) {
        if (!this.isFloating) {
          this.float();
        } else {
          this.setFloatingElementStyles();
        }
      } else if (this.isFloating) {
        this.defloat();
      }
    }
  }

  private setContainer(element: HTMLElement | string): void {
    if (typeof element === 'string') {
      const containerRef: HTMLElement = this.document.querySelector(element);
      if (containerRef) {
        this.containerReference = containerRef;
      }
    } else {
      this.containerReference = element;
    }
  }

  private addChangeSubscriptions(): void {
    const shouldUpdate$ = combineLatest([
      /* Whenever a new offset is calculated for the element (e.g. because another floating element became visible)... */
      this.floatingStackingService.getCalculatedOffset(this.floatingElement),
      /* ...or the input value of mode changed ... */
      this.mode$,
      /* ...or the containerReference dimensions changed / window is resized, the element needs to reevaluate
         if listening for scroll events is necessary and also update styles of floating element/placeholder */
      this.resizeObserver.observe$(this.containerReference).pipe(debounceTime(20), startWith(''))
    ]).pipe(takeUntil(this.unsubscribe$));

    shouldUpdate$.subscribe(([offset]) => {
      this.calculatedOffset = offset;
      this.update();
    });

    /* Register element to retrieve offset updates from service */
    this.floatingStackingService.registerElement({
      nativeElement: this.floatingElement,
      offset: this.offset$.getValue(),
      stacking: this.isFloating ? this.stacking : false
    });

    /* In case input changes also update values in FloatingStackingService */
    // delay offset$ is a workaround for element size recalculation happens too early sometimes, which causes
    // incorrect position after update offset.
    this.offset$.pipe(delay(0)).subscribe(offset => {
      this.floatingStackingService.updateValue(this.floatingElement, 'offset', offset);
    });
  }

  private createScrollSubscription(): void {
    this.scrollSubscription = fromEvent(this.scrollContainer, 'scroll', {
      passive: true
    })
      .pipe(throttleTime(0, animationFrameScheduler), takeUntil(this.unsubscribe$))
      .subscribe(_ => this.checkFloating());
  }

  private destroyScrollSubscription(): void {
    this.scrollSubscription.unsubscribe();
    delete this.scrollSubscription;
  }

  private checkFloating(updateStyles = false): void {
    const shouldFloat = this.shouldFloat();
    if (shouldFloat && !this.isFloating) {
      this.float();
    } else if (!shouldFloat && this.isFloating) {
      this.defloat();
    } else if (updateStyles) {
      this.isFloating ? this.setFloatingElementStyles() : this.removeFloatingStyles();
    }
  }

  private shouldFloat(): boolean {
    const { bottom: scrollContainerBottom } = this.getScrollContainerBounds();
    const { top: containerTop, bottom: containerBottom } = this.getBoundingClientRect(this.containerReference);
    const { height: floatingElementHeight } = this.getFloatingElementRect(false);

    const withinUpperBound =
      containerTop + this.containerThreshold + floatingElementHeight + this.calculatedOffset <= scrollContainerBottom;
    const withinLowerBound = containerBottom + (this.bottomed ? 0 : this.calculatedOffset) >= scrollContainerBottom;
    return withinLowerBound && withinUpperBound;
  }

  private getScrollContainerBounds(): { top: number; bottom: number } {
    /* Bounds depend on if scroll container is Window or a defined HTML element */
    if (this.scrollContainer instanceof HTMLElement) {
      return this.scrollContainer.getBoundingClientRect();
    } else {
      return {
        top: 0,
        bottom: this.scrollContainer.innerHeight
      };
    }
  }

  private createPlaceholder(): void {
    /* Inject placeholder */
    this.placeholder = this.renderer.createElement('div');
    this.renderer.setAttribute(this.placeholder, 'class', this.placeholderClass);
    this.setStyle('height', `${this.getFloatingElementRect(false).height}px`, this.placeholder);
    if (!this.bottomed) {
      this.setStyle('display', 'none', this.placeholder);
    }
    /* insertAdjacentElement not supported by renderer2, need to check if is browser when upgrading to universal in the future */
    this.floatingElement.insertAdjacentElement('afterend', this.placeholder);
  }

  private float(): void {
    this.floatingStackingService.updateValue(this.floatingElement, 'stacking', this.stacking);
    this.setFloatingElementStyles();
    this.isFloating = true;
    this.floatingChanged.emit(this.isFloating);
    this.document.dispatchEvent(new Event('hdms:floating_float'));
  }

  private setFloatingElementStyles(): void {
    const calculatedOffset = this.calculatedOffset;
    const { width, left, height } = this.getFloatingElementRect();
    /* If scroll container is not window but a HTMLElement we need to get its bottom offset */
    const containerOffset =
      this.scrollContainer instanceof HTMLElement
        ? this.document.documentElement.clientHeight -
          Math.round(this.getBoundingClientRect(this.scrollContainer).bottom)
        : 0;

    this.setStyle('position', 'fixed');
    if (!this.isBottomedFullWidth) {
      this.setStyle('width', `${width}px`);
      this.setStyle('left', `${left}px`);
    }
    this.setStyle('margin', '0px');
    this.setStyle('bottom', `${calculatedOffset + containerOffset}px`);

    /* Placeholder styles */
    if (this.hasPlaceholder) {
      this.setStyle('display', 'block', this.placeholder);
      this.setStyle('height', `${height}px`, this.placeholder);
    }
  }

  private defloat(): void {
    this.isFloating = false;
    this.removeFloatingStyles();
    this.floatingStackingService.updateValue(this.floatingElement, 'stacking', false);
    this.floatingChanged.emit(this.isFloating);
    this.document.dispatchEvent(new Event('hdms:floating_defloat'));
  }

  private removeFloatingStyles(): void {
    this.setStyle('position', this.bottomed ? 'absolute' : 'relative');
    this.removeStyles(['width', 'bottom', 'top', 'left', 'margin']);
    if (this.hasPlaceholder && !this.bottomed) {
      this.setStyle('display', 'none', this.placeholder);
    }
    if (this.bottomed) {
      this.setStyle('bottom', `${this.calculatedOffset}px`);
    }
  }

  private getBoundingClientRect(el: HTMLElement = this.floatingElement): DOMRect {
    return el.getBoundingClientRect();
  }

  private getFloatingElementRect(removeStyles = true): DOMRect {
    /* Temporarily remove floating styles to calculate correct values */
    if (this.isFloating && removeStyles) {
      this.removeFloatingStyles();
    }
    return this.getBoundingClientRect();
  }

  private setStyle(style: string, value: string, el: HTMLElement = this.floatingElement): void {
    this.renderer.setStyle(el, style, value);
  }

  private removeStyles(styles: string[], el: HTMLElement = this.floatingElement): void {
    styles.forEach(style => this.removeStyle(style, el));
  }

  private removeStyle(style: string, el: HTMLElement = this.floatingElement): void {
    this.renderer.removeStyle(el, style);
  }
}
